/**
 * The words of the 9 October 2026 polish: translation groups of pages, the
 * removal of a retired page, pages in the calendar, renaming and removing a
 * menu, the layout of a content type, list editors for default sections and
 * the frames of the theme. Spread into the same two trees as the other
 * dictionary files, so `dict.test.ts` checks them like the rest.
 */
import type { DictNode } from './dict.js'

export const polishIt: DictNode = {
  polish: {
    translation: {
      title: 'Lingue della pagina',
      subtitle: 'Le pagine online dello stesso gruppo si indicano a vicenda con i tag hreflang, anche nelle mappe del sito.',
      none: 'Questa pagina non è collegata a nessuna traduzione.',
      members: 'Pagine del gruppo',
      self: 'questa pagina',
      noLanguage: 'senza lingua',
      draftNote: 'Nelle mappe del sito compaiono solo le pagine online.',
      linkLabel: 'Collega a un’altra pagina',
      linkHelp: 'Scegli la stessa pagina in un’altra lingua. Una lingua può comparire una sola volta nel gruppo.',
      linkNone: 'Scegli una pagina…',
      linkNoCandidates: 'Nessuna altra pagina in una lingua diversa da collegare.',
      linkAction: 'Collega',
      unlink: 'Scollega questa pagina',
      unlinkTitle: 'Scollegare la pagina dal gruppo?',
      unlinkMessage: '«{{title}}» non sarà più indicata come traduzione delle altre pagine del gruppo, alla prossima pubblicazione.',
      linked: 'Pagina collegata al gruppo',
      unlinked: 'Pagina scollegata dal gruppo',
      reasonLink: 'Collegata come traduzione a «{{other}}»',
      reasonUnlink: 'Scollegata dal gruppo di traduzione',
    },
    deploy: {
      removeButton: 'Rimuovi dal sito ora',
      removeHelp: 'La pagina è stata ritirata ma il suo file è ancora online. Toglie solo questa pagina, con la sua riga nella mappa e i suoi reindirizzamenti.',
      removeTitle: 'Rimuovi la pagina dal sito',
      removeMessage: '«{{title}}» è già ritirata nel CMS. Questa pubblicazione parziale toglie dal sito il suo file, la sua riga nella mappa e i suoi reindirizzamenti; il resto del sito resta com’è.',
      removeConfirm: 'Rimuovi dal sito',
      removeSuccess: 'Pagina rimossa dal sito',
      removeSuccessHelp: 'Il file della pagina non è più online. Il resto del sito non è cambiato.',
      removeFailure: 'Rimozione non riuscita',
      removeReason: 'Pagina {{path}} rimossa dal sito con una pubblicazione parziale',
    },
    schedule: {
      pageChip: 'Pagina',
      pageMeta: 'Pagina · {{time}} · {{locale}} · {{path}}',
    },
    menu: {
      rename: 'Rinomina',
      renameTitle: 'Rinomina il menu',
      renameLabel: 'Nuova chiave',
      renameHelp: 'Il menu mantiene le sue voci con la nuova chiave; la vecchia sparisce dall’elenco. Il nome si scrive in minuscolo, con numeri e trattini.',
      renameAction: 'Rinomina',
      renamed: 'Menu rinominato',
      remove: 'Elimina',
      removeTitle: 'Eliminare il menu «{{key}}»?',
      removeMessage: 'Il menu esce dal sito. Resta nella cronologia e si può rimettere salvando di nuovo una chiave con lo stesso nome.',
      removeAction: 'Elimina menu',
      removed: 'Menu eliminato',
      mainLocked: 'Il menu principale è quello che il sito legge sempre: si modificano le voci, non si rinomina né si elimina.',
      checking: 'Controllo dove viene usato…',
      inUseTitle: 'Il menu è ancora in uso',
      inUseHelp: 'Finché un layout o una testata del tema lo disegna, non si può rinominare né eliminare. Cambia prima quei riferimenti.',
      usageLayout: 'Layout «{{label}}»',
      usageFrame: 'Cornice del tema «{{label}}» ({{parts}})',
      whereDraft: 'bozza',
      whereLive: 'online',
      partHead: 'head',
      partHeader: 'intestazione',
      partFooter: 'piè di pagina',
      partScripts: 'script',
      reasonRename: 'Menu «{{key}}» rinominato in «{{newKey}}»',
      reasonRemove: 'Menu «{{key}}» eliminato',
      dragHandle: 'Trascina «{{name}}» per riordinare',
      dragHelp: 'Riordina trascinando la maniglia, oppure con i pulsanti «Sposta su» e «Sposta giù».',
      moved: '«{{name}}» spostata alla posizione {{position}} di {{total}}',
    },
    contentTypeLayouts: {
      title: 'Layout per tipo di contenuto',
      subtitle: 'La cornice in cui stanno tutti i contenuti di quel tipo. Senza una scelta, i contenuti escono con il modello del tipo, come prima.',
      none: 'Nessun tipo di contenuto attivo su questo sito.',
      noLayout: 'Nessuno (modello del tipo)',
      notLive: 'Il layout «{{name}}» non è ancora pubblicato: finché non lo pubblichi i contenuti escono senza cornice.',
      missing: 'Il layout «{{key}}» scelto per questo tipo non esiste più.',
      save: 'Salva la scelta',
      saved: 'Layout del tipo salvato',
      reason: 'Layout del tipo «{{type}}»: {{layout}}',
      reasonNone: 'Tolto il layout del tipo «{{type}}»',
      goesOnline: 'La scelta vale dalla prossima pubblicazione del sito.',
    },
    sectionList: {
      title: '{{label}}',
      empty: 'Nessuna voce.',
      addItem: 'Aggiungi voce',
      addAction: 'Aggiungi pulsante',
      itemName: '{{label}} {{position}}',
      remove: 'Rimuovi',
      moveUp: 'Sposta su',
      moveDown: 'Sposta giù',
      otherFields: 'Questa voce ha altri dati (immagini o campi annidati): restano come sono.',
      fields: {
        text: 'Testo',
        href: 'Indirizzo',
        question: 'Domanda',
        answer: 'Risposta',
        title: 'Titolo',
        description: 'Descrizione',
        value: 'Valore',
        label: 'Etichetta',
        quote: 'Citazione',
        author: 'Autore',
        role: 'Ruolo',
        company: 'Azienda',
        name: 'Nome',
        url: 'Indirizzo',
        caption: 'Didascalia',
      },
    },
    theme: {
      framesHint: 'Le cornici seguono la bozza del tema: i visitatori vedono la versione online finché non pubblichi il tema. Anche il blocco del tema le protegge.',
      diffFrames: 'Cornici per lingua',
      diffFrameParts: 'Cambiano: {{parts}}',
      partHeadHtml: 'head',
      partHeaderHtml: 'intestazione',
      partFooterHtml: 'piè di pagina',
      partScriptsHtml: 'script',
    },
  },
}

export const polishEn: DictNode = {
  polish: {
    translation: {
      title: 'Languages of the page',
      subtitle: 'Live pages of the same group point at each other with hreflang tags, in the sitemaps too.',
      none: 'This page is not linked to any translation.',
      members: 'Pages in the group',
      self: 'this page',
      noLanguage: 'no language',
      draftNote: 'Only live pages appear in the sitemaps.',
      linkLabel: 'Link to another page',
      linkHelp: 'Pick the same page in another language. A language can appear only once in the group.',
      linkNone: 'Pick a page…',
      linkNoCandidates: 'No other page in a different language to link.',
      linkAction: 'Link',
      unlink: 'Unlink this page',
      unlinkTitle: 'Unlink the page from the group?',
      unlinkMessage: '“{{title}}” will no longer be listed as a translation of the other pages in the group, from the next publication.',
      linked: 'Page linked to the group',
      unlinked: 'Page unlinked from the group',
      reasonLink: 'Linked as a translation of “{{other}}”',
      reasonUnlink: 'Unlinked from the translation group',
    },
    deploy: {
      removeButton: 'Remove from the site now',
      removeHelp: 'The page was withdrawn but its file is still online. This removes only that page, with its sitemap row and its redirects.',
      removeTitle: 'Remove the page from the site',
      removeMessage: '“{{title}}” is already withdrawn in the CMS. This partial publication takes its file, its sitemap row and its redirects off the site; the rest of the site stays as it is.',
      removeConfirm: 'Remove from the site',
      removeSuccess: 'Page removed from the site',
      removeSuccessHelp: 'The page file is no longer online. The rest of the site did not change.',
      removeFailure: 'Removal failed',
      removeReason: 'Page {{path}} removed from the site with a partial publication',
    },
    schedule: {
      pageChip: 'Page',
      pageMeta: 'Page · {{time}} · {{locale}} · {{path}}',
    },
    menu: {
      rename: 'Rename',
      renameTitle: 'Rename the menu',
      renameLabel: 'New key',
      renameHelp: 'The menu keeps its items under the new key; the old one leaves the list. Use lowercase letters, digits and hyphens.',
      renameAction: 'Rename',
      renamed: 'Menu renamed',
      remove: 'Delete',
      removeTitle: 'Delete the “{{key}}” menu?',
      removeMessage: 'The menu leaves the site. It stays in the history and comes back if you save a menu with the same key again.',
      removeAction: 'Delete menu',
      removed: 'Menu deleted',
      mainLocked: 'The main menu is the one the site always reads: change its items, but it cannot be renamed or deleted.',
      checking: 'Checking where it is used…',
      inUseTitle: 'The menu is still in use',
      inUseHelp: 'While a layout or a theme header draws it, it cannot be renamed or deleted. Change those references first.',
      usageLayout: 'Layout “{{label}}”',
      usageFrame: 'Theme frame “{{label}}” ({{parts}})',
      whereDraft: 'draft',
      whereLive: 'live',
      partHead: 'head',
      partHeader: 'header',
      partFooter: 'footer',
      partScripts: 'scripts',
      reasonRename: 'Menu “{{key}}” renamed to “{{newKey}}”',
      reasonRemove: 'Menu “{{key}}” deleted',
      dragHandle: 'Drag “{{name}}” to reorder',
      dragHelp: 'Reorder by dragging the handle, or with the “Move up” and “Move down” buttons.',
      moved: '“{{name}}” moved to position {{position}} of {{total}}',
    },
    contentTypeLayouts: {
      title: 'Layout by content type',
      subtitle: 'The frame all the contents of that type sit in. With no choice, contents come out with the type template, as before.',
      none: 'No content type is active on this site.',
      noLayout: 'None (type template)',
      notLive: 'The “{{name}}” layout is not published yet: until you publish it, contents come out without a frame.',
      missing: 'The “{{key}}” layout chosen for this type no longer exists.',
      save: 'Save the choice',
      saved: 'Layout of the type saved',
      reason: 'Layout of the “{{type}}” type: {{layout}}',
      reasonNone: 'Layout of the “{{type}}” type removed',
      goesOnline: 'The choice applies from the next publication of the site.',
    },
    sectionList: {
      title: '{{label}}',
      empty: 'No entries.',
      addItem: 'Add entry',
      addAction: 'Add button',
      itemName: '{{label}} {{position}}',
      remove: 'Remove',
      moveUp: 'Move up',
      moveDown: 'Move down',
      otherFields: 'This entry has other data (images or nested fields): it stays as it is.',
      fields: {
        text: 'Text',
        href: 'Address',
        question: 'Question',
        answer: 'Answer',
        title: 'Title',
        description: 'Description',
        value: 'Value',
        label: 'Label',
        quote: 'Quote',
        author: 'Author',
        role: 'Role',
        company: 'Company',
        name: 'Name',
        url: 'Address',
        caption: 'Caption',
      },
    },
    theme: {
      framesHint: 'Frames follow the theme draft: visitors see the live version until you publish the theme. The theme lock protects them too.',
      diffFrames: 'Frames by language',
      diffFrameParts: 'Changed: {{parts}}',
      partHeadHtml: 'head',
      partHeaderHtml: 'header',
      partFooterHtml: 'footer',
      partScriptsHtml: 'scripts',
    },
  },
}
